import { useState } from 'react';
import { Typography, Grid, Button, Theme, Link, Box } from '@mui/material';
import { DropzoneRootProps, DropzoneInputProps } from 'react-dropzone';
import { DocumentTypes } from 'types';
import BackgroundBorder from './BackgroundBorder';
import { makeStyles } from 'tss-react/mui';
import { UploadIcon } from 'components/icons';
import { getStyleVariables } from 'styles/vars';
import { GVTypography } from 'components/lib';
import { useFlags } from 'launchdarkly-react-client-sdk';

const RETENTION_MESSAGE =
  "Files in this inspection may no longer be available. This can happen if a file was manually removed or deleted by your organization's data retention policy.";
import UploadFromUrlModal from './UploadFromUrlModal';
import { useDispatch } from 'react-redux';
import { startHtmlToPdf } from 'store/requests';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';

interface DragAndDropBackground {
  document: DocumentTypes;
  title: string;
  handleShowLibrary: () => void;
  getRootProps: (props?: DropzoneRootProps | undefined) => DropzoneRootProps;
  getInputProps: (props?: DropzoneInputProps | undefined) => DropzoneInputProps;
  open: () => void;
  dragging: boolean;
  onOpenedFromHtml?: (isGvault: boolean) => void;
  setRemoveHeadersFooters: (value: boolean) => void;
  removeHeadersFooters: boolean;
  setAutoSelectLayersSeparations: (value: boolean) => void;
  autoSelectLayersSeparations: boolean;
  multipleLocations: boolean;
  setMultipleLocations: (value: boolean) => void;
  uploadDisabled?: boolean;
  showRetentionMessage?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    dropzone: {
      display: 'flex',
      flex: 1,
      overflowY: 'auto',
    },
    button: {
      marginTop: '24px',
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      color: theme.palette.text.primary,
      border: '1px solid black',
      background: styleVariables.colors.menuLightGrey,
      boxShadow: 'none',
      '&:hover': {
        background: styleVariables.colors.lightGrey,
        boxShadow: 'none',
      },
    },
    textContainer: {
      marginTop: '2vh',
    },
    textButton: {
      textDecoration: 'underline',
      textTransform: 'none',
      opacity: '1',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    text: {
      fontSize: 12,
      fontWeight: 600,
    },
  };
});

const DragAndDropBackground = (props: DragAndDropBackground) => {
  const {
    getRootProps,
    getInputProps,
    open,
    handleShowLibrary,
    dragging,
    removeHeadersFooters,
    setRemoveHeadersFooters,
    autoSelectLayersSeparations,
    setAutoSelectLayersSeparations,
    multipleLocations,
    setMultipleLocations,
    uploadDisabled,
    showRetentionMessage,
  } = props;
  const { classes } = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const [isUploadFromUrlOpen, setUploadFromUrlOpen] = useState<boolean>(false);
  const flags = useFlags();
  const pageDimensionLimit = flags.pageDimensionLimit || 2400;
  const pageCountLimit = flags.pageCountLimit || 200;
  const documentSizeLimitInGb = flags.documentSizeLimitInGb || 1;
  const dispatch = useDispatch();

  const handleSubmitUrlAsFile = (url: string) => {
    dispatch(startHtmlToPdf(url, props.document));
    if (props.onOpenedFromHtml) {
      props.onOpenedFromHtml(!url.startsWith('http'));
    }
  };

  if (uploadDisabled) {
    return (
      <div className={classes.dropzone}>
        <BackgroundBorder />
        {showRetentionMessage && (
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ position: 'relative', zIndex: 1, padding: '16px' }}
          >
            <GVTypography
              color="primary"
              emphasis="disabled"
              sx={{ padding: '36px', textAlign: 'center', fontSize: 12, maxWidth: '740px' }}
            >
              {RETENTION_MESSAGE}
            </GVTypography>
          </Grid>
        )}
      </div>
    );
  }

  return (
    <div className={classes.dropzone} {...getRootProps()} onDoubleClick={open}>
      <input id={`upload-${props.document}`} {...getInputProps()} />
      <BackgroundBorder animate={isHovered || dragging} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          overflowY: 'auto',
          padding: '16px',
        }}
      >
        {showRetentionMessage && (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <GVTypography
              color="primary"
              emphasis="disabled"
              sx={{ padding: '36px', textAlign: 'center', fontSize: 12, maxWidth: '740px' }}
            >
              {RETENTION_MESSAGE}
            </GVTypography>
          </Grid>
        )}
        <UploadIcon animate={isHovered || dragging} />
        <Grid
          className={classes.textContainer}
          container
          flexDirection={'row'}
          justifyContent={'center'}
          alignItems={'flex-end'}
          gap={'0.4rem'}
        >
          <Typography color="primary" className={classes.text}>
            {'Drag & drop your file(s), '}
          </Typography>
          <Button
            className={classes.textButton}
            onClick={open}
            onMouseEnter={() => {
              setIsHovered(true);
            }}
            onMouseLeave={() => {
              setIsHovered(false);
            }}
            data-testid={`${props.document}_button_browse`}
          >
            <Typography color="primary" className={classes.text}>
              Browse File(s),
            </Typography>
          </Button>
          {!flags.uploadFromUrlEnabled && (
            <Typography color="primary" className={classes.text}>
              or
            </Typography>
          )}
        </Grid>
        {flags.uploadFromUrlEnabled && (
          <Grid container flexDirection={'row'} justifyContent={'center'} alignItems={'flex-end'} gap={'0.4rem'}>
            <Button
              className={classes.textButton}
              onClick={() => setUploadFromUrlOpen(true)}
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsHovered(false);
              }}
              data-testid={`${props.document}_button_open_from_url`}
            >
              <Typography color="primary" className={classes.text}>
                Open from URL,
              </Typography>
            </Button>
            <Typography color="primary" className={classes.text}>
              or
            </Typography>
          </Grid>
        )}
        <Button
          size="large"
          variant="contained"
          className={classes.button}
          onClick={handleShowLibrary}
          data-testid={`${props.document}_button_library`}
        >
          Open Library
        </Button>
        <Box sx={{ marginTop: '16px' }}>
          <GVCheckbox
            value={'remove headers/footers'}
            checked={removeHeadersFooters}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRemoveHeadersFooters(!removeHeadersFooters);
            }}
            size="small"
            label="Remove headers and footers (Word files only)"
          />
        </Box>
        {flags.showAutoSelectLayersSeparations && (
          <Box sx={{ marginTop: '8px' }}>
            <GVCheckbox
              data-testid={`${props.document}_auto_layers_separations`}
              value={'auto layers/separations'}
              checked={autoSelectLayersSeparations}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setAutoSelectLayersSeparations(!autoSelectLayersSeparations);
              }}
              size="small"
              label="Auto select layers and separations"
            />
          </Box>
        )}
        <Box sx={{ marginTop: '8px' }}>
          <GVCheckbox
            value={'multiple locations'}
            checked={multipleLocations}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMultipleLocations(!multipleLocations);
            }}
            size="small"
            label="Uploading from multiple locations?"
          />
        </Box>
        <GVTypography color="primary" emphasis="disabled" sx={{ padding: '32px', marginTop: '8px' }}>
          We recommend files be under {pageCountLimit} pages, {documentSizeLimitInGb} GB, and{' '}
          {((pageDimensionLimit * 300) / 72).toLocaleString('en-US')} pixels (300 dpi) in height or width per page.
          Learn more{' '}
          <Link
            target="_blank"
            href={import.meta.env.VITE_SIZE_LIMIT_DOCUMENTATION_URL || 'https://help.globalvision.co/verify'}
          >
            here
          </Link>
          .
        </GVTypography>
        <UploadFromUrlModal
          open={isUploadFromUrlOpen}
          handleSubmit={handleSubmitUrlAsFile}
          handleClose={() => setUploadFromUrlOpen(false)}
        />
      </Box>
    </div>
  );
};

export default DragAndDropBackground;
